import { create } from 'zustand';
import type { DeployedRelease } from '../ports/AppUpdater';

/**
 * The update offer: a newer build is on the server and this page is not it.
 *
 * `offered` is the version the server named, or WORKER when only the service
 * worker knew (a new script installed before `/version.json` was asked). It is
 * set once per version and never cleared by a later check, so the banner shows
 * once and stays until the person taps Update. A check every five minutes must
 * not make it blink, re-announce or come back after it has gone.
 */
export const WORKER = 'worker';

interface AppUpdateState {
  offered: string | null;
  /**
   * The release that build is, when the server said: what the banner names.
   * Null for the worker's signal and for a server that names no release. The
   * request of 5 October 2026 (third), section 8.
   */
  offeredRelease: DeployedRelease | null;
  applying: boolean;
  offer: (version: string, release?: DeployedRelease | null) => void;
  setApplying: (applying: boolean) => void;
}

export const useAppUpdateStore = create<AppUpdateState>((set) => ({
  offered: null,
  offeredRelease: null,
  applying: false,
  offer: (version, release = null) =>
    set((state) => {
      if (state.offered === version) return state;
      // A named version is better information than the worker's signal, but
      // either one is already on screen: swapping the label must not re-render
      // the banner as though it were new.
      if (state.offered !== null && version === WORKER) return state;
      return { offered: version, offeredRelease: release };
    }),
  setApplying: (applying) => set({ applying }),
}));
